/**
 * Publication d'un défi EN ARRIÈRE-PLAN : l'envoi de la vidéo peut prendre du temps, il ne doit pas bloquer
 * la fenêtre « Lancer un défi ». La tâche vit dans ce module (pas dans un composant) : on ferme la fenêtre,
 * on change de page dans l'app, elle continue. À la fin, un message dans l'app (`ChallengePublishToast`)
 * dit que le défi est publié, et le Feed est rechargé pour qu'il y apparaisse.
 *
 * Fermer l'onglet ou l'app arrête l'envoi (le navigateur coupe tout) : on prévient avant de quitter.
 */
import { queryClient } from '@/lib/query-client';
import { buildChallengeUrl, publishChallenge } from './challenge-api';

export type PublishParams = Parameters<typeof publishChallenge>[0];

export type BackgroundPublish =
  | { id: number; status: 'publishing'; title: string }
  | { id: number; status: 'published'; title: string; slug: string; url: string }
  | { id: number; status: 'failed'; title: string; error: string; params: PublishParams };

/** Temps d'affichage du message « Ton défi est publié ! ». */
export const PUBLISHED_TOAST_MS = 10_000;

let jobs: BackgroundPublish[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function set(next: BackgroundPublish[]) {
  jobs = next;
  updateUnloadGuard();
  listeners.forEach((l) => l());
}

function replace(id: number, job: BackgroundPublish) {
  set(jobs.map((j) => (j.id === id ? job : j)));
}

export function subscribeBackgroundPublish(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getBackgroundPublishes(): BackgroundPublish[] {
  return jobs;
}

/** Lance la publication et rend la main tout de suite. */
export function startBackgroundPublish(
  params: PublishParams,
  publish: typeof publishChallenge = publishChallenge,
): number {
  const id = nextId++;
  const title = params.payload.title || 'Ton défi';
  set([...jobs, { id, status: 'publishing', title }]);
  void publish(params).then(
    (res) => {
      replace(id, {
        id,
        status: 'published',
        title,
        slug: res.slug,
        url: res.challengeUrl || buildChallengeUrl(res.slug),
      });
      // le défi apparaît dans le Feed
      void queryClient.invalidateQueries({ queryKey: ['news-challenges'] });
      // le message de réussite s'efface tout seul
      setTimeout(() => dismissBackgroundPublish(id), PUBLISHED_TOAST_MS);
    },
    (err: unknown) => {
      replace(id, {
        id,
        status: 'failed',
        title,
        error: err instanceof Error ? err.message : 'Erreur lors de la publication',
        params,
      });
    },
  );
  return id;
}

export function retryBackgroundPublish(id: number): void {
  const job = jobs.find((j) => j.id === id);
  if (job?.status !== 'failed') return;
  dismissBackgroundPublish(id);
  startBackgroundPublish(job.params);
}

export function dismissBackgroundPublish(id: number): void {
  set(jobs.filter((j) => j.id !== id));
}

// ─── Fermer l'onglet pendant un envoi : demander confirmation ────────────────

let guarded = false;
function onBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
  e.returnValue = '';
}
function updateUnloadGuard() {
  if (typeof window === 'undefined') return;
  const busy = jobs.some((j) => j.status === 'publishing');
  if (busy && !guarded) window.addEventListener('beforeunload', onBeforeUnload);
  if (!busy && guarded) window.removeEventListener('beforeunload', onBeforeUnload);
  guarded = busy;
}

/** Tests seulement. */
export function resetBackgroundPublishes(): void {
  set([]);
  nextId = 1;
}
